import { ProductFormFields } from "@design-controls/types";
import {
  QFlex,
  QFormControl,
  QIcon,
  QInput,
  QSelect,
  QSelectItem,
  QStack,
  useToastProvider,
} from "@qualio/ui-components";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useProductQuickSearch } from "../../hooks/search/useProductQuickSearch";
import { useResolveQriDetails } from "../../hooks/useResolveQriDetails";

const CREATE_NEW_PRODUCT = "__NEW__";

/**
 * Picks the Resource Library product a project is linked to, or captures the
 * details of a new one to create alongside the project.
 */
export const ResourceLibraryProductField: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  // `getFieldState` is path-based, so it narrows across the discriminated
  // union. `formState.errors` does not, it only gives us shared keys.
  const { watch, setValue, resetField, trigger, getFieldState, formState } =
    useFormContext<ProductFormFields>();

  const { isLoading, isError, error, data } = useProductQuickSearch();

  const productSource = watch("productSource");
  const productQri = watch("productQri");

  useEffect(() => {
    if (!isError) {
      return;
    }
    showToast({
      id: "resource-library-product-field-error",
      replace: true,
      status: "error",
      title: t("products.profile.resourceLibraryProductField.errorTitle"),
      description: t(
        "products.profile.resourceLibraryProductField.errorDescription",
      ),
    });
    console.error(
      "Error fetching products for ResourceLibraryProductField:",
      error,
    );
  }, [isError, error, showToast, t]);

  // Quick search only returns what it can see: a project can be linked to a
  // product that has since been archived, or that sits beyond the pages we
  // fetched. Without this the picker renders blank on a linked project.
  const linkedQri = productSource === "existing" ? productQri : undefined;
  const missingQri =
    linkedQri && !isLoading && !data.some((item) => item.id === linkedQri)
      ? linkedQri
      : undefined;

  const { details: resolvedDetails, isLoading: isResolvingMissingQri } =
    useResolveQriDetails(missingQri ? [missingQri] : []);

  // Omitting the option while unresolved (rather than showing the raw QRI)
  // lets QSelect fall back to its placeholder; `isLoading` below adds the
  // spinner on top.
  const resolvedMissingQri = missingQri
    ? resolvedDetails[missingQri]
    : undefined;

  const options = useMemo<Array<QSelectItem<ReactNode, string>>>(
    () => [
      ...(missingQri && resolvedMissingQri
        ? [
            {
              label: resolvedMissingQri.name,
              code: resolvedMissingQri.code,
              value: missingQri,
            },
          ]
        : []),
      ...data.map((item) => ({
        label: item.label,
        code: item.code,
        value: item.id,
      })),
      {
        label: (
          <QFlex direction="row" align="center" gap={2}>
            <QFlex align="center" pb="2px">
              <QIcon iconName="Plus" />
            </QFlex>
            {t("products.profile.resourceLibraryProductField.createNew")}
          </QFlex>
        ),
        value: CREATE_NEW_PRODUCT,
      },
    ],
    [data, t, missingQri, resolvedMissingQri],
  );

  // QSelect doesn't forward `onInputChange`; `filterOption` is the only prop
  // that sees the typed text. Stashed here to prefill the new-product inputs.
  const filterInputRef = useRef("");

  // The QSelect drives the `productSource` discriminant, which decides which
  // other fields exist on the form at all (so we cannot use a plain
  // Controller over a single field name). `setValue` does not revalidate on
  // its own, and the wizard's Next button is gated on `isValid`, hence the
  // explicit `trigger` once the branch is consistent.
  const handleSelectChange = useCallback(
    (selected: QSelectItem<ReactNode, string> | null) => {
      if (selected === null) {
        setValue("productSource", "none");
        resetField("resourceLibraryProductToCreate");
      } else if (selected.value === CREATE_NEW_PRODUCT) {
        setValue("productSource", "new");
        setValue("resourceLibraryProductToCreate", {
          name: filterInputRef.current,
          displayId: filterInputRef.current,
        });
      } else {
        setValue("productSource", "existing");
        setValue("productQri", selected.value);
        resetField("resourceLibraryProductToCreate");
      }
      filterInputRef.current = "";
      void trigger("productSource");
    },
    [setValue, resetField, trigger],
  );

  const filterOption = useCallback(
    (option: { label: string; value: string }, inputValue: string): boolean => {
      filterInputRef.current = inputValue;
      if (option.value === CREATE_NEW_PRODUCT) {
        return true;
      }
      return option.label.toLowerCase().includes(inputValue.toLowerCase());
    },
    [],
  );

  const nameState = getFieldState(
    "resourceLibraryProductToCreate.name",
    formState,
  );
  const displayIdState = getFieldState(
    "resourceLibraryProductToCreate.displayId",
    formState,
  );

  // Read off the discriminant rather than `productQri`: clearing leaves a stale
  // QRI in the form values, and the `none` branch strips it on validation.
  const selectedValue = useMemo(() => {
    if (productSource === "new") {
      return CREATE_NEW_PRODUCT;
    }
    if (productSource === "existing") {
      return productQri ?? undefined;
    }
    return undefined;
  }, [productSource, productQri]);

  return (
    <QStack gap={4}>
      <QSelect
        isLoading={isLoading || isResolvingMissingQri}
        options={options}
        isSearchable
        isClearable
        value={selectedValue}
        onChange={handleSelectChange}
        filterOption={filterOption}
      />
      {productSource === "new" && (
        <QStack direction="row" pl={4}>
          <QFormControl
            label={t(
              "products.profile.resourceLibraryProductField.newDisplayId",
            )}
            isRequired
            error={
              displayIdState.error?.message && t(displayIdState.error.message)
            }
            isInvalid={!!displayIdState.error}
          >
            <Controller
              name="resourceLibraryProductToCreate.displayId"
              render={({ field: { value, onChange } }) => (
                <QInput
                  value={value ?? ""}
                  onChange={onChange}
                  data-cy="rl-product-display-id"
                />
              )}
            />
          </QFormControl>
          <QFormControl
            label={t("products.profile.resourceLibraryProductField.newName")}
            isRequired
            error={nameState.error?.message && t(nameState.error.message)}
            isInvalid={!!nameState.error}
          >
            <Controller
              name="resourceLibraryProductToCreate.name"
              render={({ field: { value, onChange } }) => (
                <QInput
                  value={value ?? ""}
                  onChange={onChange}
                  data-cy="rl-product-name"
                />
              )}
            />
          </QFormControl>
        </QStack>
      )}
    </QStack>
  );
};
